import { observable, action, computed, reaction } from 'mobx';
import $ from 'jquery';
import axios, { CancelToken } from 'axios';
import _ from 'lodash';
import ProviderLabelData from './ProviderLabelStore';
import { getYearOptions } from '../helpers/helpers';

const mortgageTypes = [
  { id: 1, name: 'Annuitair', value: 6 },
  { id: 2, name: 'Lineair', value: 7 },
  { id: 3, name: 'Aflosvrij', value: 1 },
];

const loanpartDurationLabels = getYearOptions();


class ComparisonFormStore {
  /* Static label and provider names  */
  firstLabelName = 'first-selected-label';
  secondLabelName = 'second-selected-label';
  firstProviderName = 'first-selected-provider';
  secondProviderName = 'second-selected-provider';

  @observable accessor emptyFrtError = false;

  @observable accessor previousScreenWidth;
  @observable accessor calculationResponseLoading = false;
  @observable accessor responseError = '';
  @observable accessor errorFields = [];
  @observable accessor errorFieldKeys = [];

  @observable accessor firstProviderId = 0;
  @observable accessor firstLabelId = 0;
  @observable accessor secondProviderId = 0;
  @observable accessor secondLabelId = 0;

  @observable accessor providers = [];
  @observable accessor labels = [];

  /* By default give filteredLabels value + id */
  @observable accessor firstFilteredLabels = [{ id: 0, name: '', value: 0 }];
  @observable accessor secondFilteredLabels = [{ id: 0, name: '', value: 0 }];

  @observable accessor nhg = false;
  @observable accessor houseValue = 330000;
  @observable accessor mortgage = 330000;
  @observable accessor loanparts = [
    330000,
  ];

  @observable accessor firstFixedRateTerms = [[]];
  @observable accessor secondFixedRateTerms = [[]];
  @observable accessor fixedRateTerms = [[]];
  @observable accessor loanpartsPostData = [];

  @observable accessor comparedProviderData = [];

  constructor() {
    this.providers = ProviderLabelData.allProviders;
    this.labels = ProviderLabelData.allLabels;

    this.toggleNHG = this.toggleNHG.bind(this);
    this.setDesiredMortgage = this.setDesiredMortgage.bind(this);
    this.setHouseValue = this.setHouseValue.bind(this);
    this.setLoanpart = this.setLoanpart.bind(this);
    this.setLoanparts = this.setLoanparts.bind(this);
    this.setLoanpartsPostData = this.setLoanpartsPostData.bind(this);
    this.fitLoanparts = this.fitLoanparts.bind(this);
    this.resetSelectedLabelText = this.resetSelectedLabelText.bind(this);
    this.setProvider = this.setProvider.bind(this);
    this.setLabel = this.setLabel.bind(this);
    this.setLabelAfterProvider = this.setLabelAfterProvider.bind(this);
    this.resetLoanpartsPostData = this.resetLoanpartsPostData.bind(this);
    this.filterLabelsByProviderId = this.filterLabelsByProviderId.bind(this);
    this.cancelRequest = this.cancelRequest.bind(this);
    this.setBasePostData = this.setBasePostData.bind(this);
    this.getFixedRateTermsForLabel = this.getFixedRateTermsForLabel.bind(this);
    this.getFixedRateTerms = _.debounce(this.getFixedRateTerms.bind(this), 600);
    this.setFixedRateTerms = this.setFixedRateTerms.bind(this);
    this.compareProviders = this.compareProviders.bind(this);
    this.getCompareProvidersPostData = this.getCompareProvidersPostData.bind(this);
    this.clearErrorFields = this.clearErrorFields.bind(this);

    reaction(
      () => [ProviderLabelData.allProviders, ProviderLabelData.allLabels],
      (providersAndLabels) => {
        if (providersAndLabels[0].length > 0 && providersAndLabels[1].length > 0) {
          this.setProvider(this.firstProviderId, this.firstProviderName);
          this.setProvider(this.secondProviderId, this.secondProviderName);
        }
      },
    );
  }

  @action setProvider(value, providerName) {
    const providerId = Number(value);

    if (providerName === this.firstProviderName) {
      this.firstProviderId = providerId;
    } else if (providerName === this.secondProviderName) {
      this.secondProviderId = providerId;
    }

    this.filterLabelsByProviderId(providerId, providerName);
    this.setLabelAfterProvider(providerName);

    /* Reset loanpartsPostData */
    this.resetLoanpartsPostData();
  }

  @action setLabel(value, labelName) {
    if (labelName === this.firstLabelName) {
      this.firstLabelId = Number(value);
    } else if (labelName === this.secondLabelName) {
      this.secondLabelId = Number(value);
    }
  }

  @action setLabelAfterProvider(providerName) {
    if (providerName === this.firstProviderName) {
      const firstFilteredLabel = this.firstFilteredLabels.length > 0 && this.firstFilteredLabels[0];
      if (firstFilteredLabel) {
        this.setLabel(firstFilteredLabel.id, this.firstLabelName);
      }
    } else if (providerName === this.secondProviderName) {
      const secondFilteredLabel = this.secondFilteredLabels.length > 0 && this.secondFilteredLabels[0];
      if (secondFilteredLabel) {
        this.setLabel(secondFilteredLabel.id, this.secondLabelName);
      }
    }
  }

  @action toggleNHG(e) {
    this.nhg = e.target.checked;
    this.getFixedRateTerms();
  }

  @action setDesiredMortgage(value) {
    this.mortgage = value;
    this.getFixedRateTerms();
  }

  @action setHouseValue(value) {
    this.houseValue = value;
    this.getFixedRateTerms();
  }

  @action getFixedRateTerms() {
    if (this.loanparts.length > 0) {
      this.getFixedRateTermsForLabel(0, this.firstLabelId, this.firstLabelName)
        .then(() => this.getFixedRateTermsForLabel(0, this.secondLabelId, this.secondLabelName))
        .then(() => this.getFixedRateTermsForLabel(1, this.firstLabelId, this.firstLabelName))
        .then(() => this.getFixedRateTermsForLabel(1, this.secondLabelId, this.secondLabelName))
        .then(() => this.getFixedRateTermsForLabel(2, this.firstLabelId, this.firstLabelName))
        .then(() => this.getFixedRateTermsForLabel(2, this.secondLabelId, this.secondLabelName));
    }
  }

  @action
  // eslint-disable-next-line consistent-return
  async getFixedRateTermsForLabel(index, labelId, labelName) {
    const basePostData = this.setBasePostData(index, labelId);
    let responseObject;
    const self = this;

    if (this.loanpartsPostData.length >= index + 1) {
      const postData = {
        ...basePostData,
      };

      /* Set cancelToken and cancel current request. */
      this.cancelRequest();
      if (_.keys(basePostData).length > 0) {
        responseObject = await axios.post('/label/fixed-rate-term', postData, {
          cancelToken: CancelToken.source().token,
        })
        .then((response) => {
          if (labelName === self.firstLabelName) {
            self.responseError = '';
          }

          if (response.data.length === 0) {
            self.responseError = `We hebben geen rentevaste periode kunnen 
        vinden voor de geselecteerde aflosvorm, mogelijk komt dit door de ltv.`;
          }

          this.setFixedRateTerms(labelName, index, response);

          return response.data;
        })
        .catch(err => this.errorMessage = err);

        return _.uniqBy(responseObject);
      }
    }
  }

  /* Helper method for getFixedRateTermsForLabel */
  @action setFixedRateTerms(labelName, index, response) {
    /* Filter dups if endpoint delivers duplicates */
    if (labelName === this.firstLabelName) {
      this.firstFixedRateTerms[index] = _.uniqBy(response.data);
    } else if (labelName === this.secondLabelName) {
      this.secondFixedRateTerms[index] = _.uniqBy(response.data);
    }
  }

  @action
  async compareProviders() {
    let responseObject;
    this.calculationResponseLoading = true;
    this.comparedProviderData = '';
    const compareProvidersPostData = this.getCompareProvidersPostData();

    if (!_.isEmpty(compareProvidersPostData) &&
        !compareProvidersPostData.loanparts.some(loanpart => _.isEmpty(loanpart))) {
      this.cancelRequest();
      responseObject = await axios.post('/calculate/comparison', compareProvidersPostData, {
        cancelToken: CancelToken.source().token,
      })
      .then((response) => {
        this.clearErrorFields();
        this.comparedProviderData = response.data;
        return response;
      })
      .catch((error) => {
        if (_.get(error, 'response.status') === 422) {
          this.responseError = 'De volgende velden zijn foutief ingevuld:';
          this.errorFields = _.values(error.response.data);
          this.errorFieldKeys = _.keys(error.response.data);
          this.comparedProviderData = '';
        } else {
          this.responseError = 'Er is iets fout gegaan met de berekening. Probeer het op een later tijdstip.';
        }
      });
    }

    this.calculationResponseLoading = false;
    return responseObject;
  }

  /* Helper method for compareProviders */
  @action clearErrorFields() {
    this.responseError = '';
    this.errorFieldKeys = [];
    this.errorFields = [];
  }

  @action getCompareProvidersPostData() {
    let compareProvidersPostData = {};
    const labels = [this.firstLabelId, this.secondLabelId];
    const {
      mortgage,
      houseValue,
      nhg,
      loanparts,
    } = this;

    /* Check for empty labels */
    const emptyLabels = labels.some(label => !label);

    /* Setup loanparts post data */
    const loanpartPostData = this.loanpartsPostData.map((postDataObject, index) => {
      let postData = {
        ...postDataObject,
        gross: loanparts[index],
      };

      /* Check if one loanpart is lower than 1000 */
      if (loanparts[index] < 1000) {
        this.responseError = 'De hoogte van een leningdeel mag niet lager zijn dan €1.000,-';
        postData = {};
      }

      return postData;
    });

    /* Check if all form values are correctly filled in. */
    if (mortgage &&
      houseValue &&
      nhg !== undefined &&
      loanparts &&
      !emptyLabels) {
      compareProvidersPostData = {
        labels,
        nhg,
        houseValue,
        mortgage,
        loanparts: loanpartPostData,
      };
    } else {
      this.responseError = 'Niet alle velden zijn correct ingevuld.';
    }

    return compareProvidersPostData;
  }


  @computed get intersectedRateTerms() {
    let intersectedRateTerms = [];

    if (this.firstFixedRateTerms.length > 0 &&
      this.secondFixedRateTerms.length > 0 &&
      this.firstFixedRateTerms.length === this.secondFixedRateTerms.length) {
      intersectedRateTerms = this.firstFixedRateTerms
        .map((firstFixedRateTermsObject, index) => firstFixedRateTermsObject.filter(firstFixedRateTermsItem => (
            this.secondFixedRateTerms[index].indexOf(firstFixedRateTermsItem) > -1
        ))
        .map(term => ({ id: term, name: `${term} Jaar`, value: Number(term) }))
        .filter((term) => {
          let returnVal = true;
          if (this.loanpartsPostData.length > index) {
            returnVal = term.value <= Number(this.loanpartsPostData[index].loanpartDuration) / 12;
          }

          return returnVal;
        }));
    }

    return intersectedRateTerms;
  }

  @computed get loanpartDurationLabels() {
    return loanpartDurationLabels;
  }

  @action cancelRequest() {
    if (_.has(this.abortController, 'abort')) {
      /* Always clear pending request. */
      this.abortController.abort();
    }

    this.abortController = new AbortController();
  }

  @action setBasePostData(index, labelId) {
    let postData = {};
    let mortgageType = 6;

    if (this.loanpartsPostData.length > index) {
      mortgageType = _.has(this.loanpartsPostData[index], 'mortgageType') &&
        this.loanpartsPostData[index].mortgageType;
    }

    if (labelId && this.mortgage > 4999 && this.nhg !== undefined && mortgageType) {
      postData = {
        labelId,
        houseValue: this.houseValue,
        mortgage: this.mortgage,
        nhg: this.nhg,
        mortgageType,
      };
    }

    if (this.mortgage < 5000) {
      this.responseError = 'Gewenste hypotheek bedrag mag niet lager zijn dan €5.000,-';
    }

    return postData;
  }

  @action filterLabelsByProviderId(providerId, providerName) {
    if (providerName === this.firstProviderName) {
      this.firstFilteredLabels = ProviderLabelData.allLabels.filter(label => label.providerId === providerId);
    } else if (providerName === this.secondProviderName) {
      this.secondFilteredLabels = ProviderLabelData.allLabels.filter(label => label.providerId === providerId);
    }
  }

  @action resetSelectedLabelText(labelName) {
    /* Reset label id */
    if (labelName === this.firstLabelName) {
      this.firstLabelId = '';
    } else if (labelName === this.secondLabelName) {
      this.secondLabelId = '';
    }

    $(`select[name=${labelName}]`).siblings('.text')
      .text('Maak aub een keuze');
  }

  @computed get mortgageTypes() {
    return mortgageTypes;
  }

  @action setLoanpart(value, index) {
    const loanparts = this.loanparts;
    loanparts[index] = value;
  }

  @action setLoanparts(value, index) {
    const mortgage = this.mortgage;
    const loanparts = this.loanparts;
    const maxLoanparts = 3;

    /* If loanparts are empty set initial loanpart equal to mortgage */
    if (loanparts.length <= 1) {
      loanparts[0] = Number(mortgage);
    }

    /* Input LoanpartForm */

    /*
     * If loanparts is not empty and and index and value is given check mortgage - sum
      * */
    if (loanparts.length >= 1) {
      /* Add a loanpart input from LoanpartFormComponent to mortgageForm.loanparts */
      loanparts[index] = Number(value);
      const difference = mortgage - loanparts.reduce((a, b) => a + b);

      /*
       *   Loanparts < mortgage
       */

      /* Push difference to loanparts or if maxLoanparts in reached, add val to end of array */
      if (difference > 0 && loanparts.length < maxLoanparts) {
        loanparts.push(Number(difference));

        /* If maxLoanparts is reached add difference to last loanparts item */
      } else if (difference > 0 && loanparts.length === maxLoanparts) {
        loanparts[maxLoanparts - 1] += difference;

        /*
        *   Loanparts > mortgage
        */

        /*  Subtract difference from last loanpart  */
      } else if (difference <= 0) {
        /* First remove absolute value of difference  from last loanpart */
        loanparts[loanparts.length - 1] -= Math.abs(difference);

        /* Make last loanpart > 0 */
        this.fitLoanparts(loanparts);
      }
    }

    /* Set loanpartsPostData */
    this.setLoanpartsPostData(this.loanpartsPostData, loanparts.length);
  }

  @action setLoanpartsPostData(loanpartsPostData, loanpartsLength) {
    this.loanpartsPostData = loanpartsPostData
      .filter((loanpartPostDataItem, index) => index <= loanpartsLength - 1);
  }

  @action resetLoanpartsPostData() {
    const loanpartsPostData = this.loanpartsPostData.slice();
    this.loanpartsPostData = loanpartsPostData.map((postItem) => {
      const newPostItem = { ...postItem };
      newPostItem.frt = '';
      newPostItem.gross = '';
      newPostItem.interest = '-';

      return newPostItem;
    });
  }

  /**
   *
   * @param loanparts
   */
  @action fitLoanparts(loanparts) {
    const lastIndex = loanparts.length - 1;
    let lastLoanpart = loanparts[lastIndex];

    if (loanparts[lastIndex] <= 0) {
      /* Current last loanparts item */
      let curLoanpartIndex = lastIndex;

      /*
       *  If last becomes negative, take last items absolute value,
       *   loop over loanparts, pop last item and subtract abs value from
       *   the new last index. Repeat this till last index is positive.
       */
      while (lastLoanpart <= 0) {
        curLoanpartIndex -= 1;
        loanparts[curLoanpartIndex] -= Math.abs(lastLoanpart);
        loanparts.pop();
        lastLoanpart = loanparts[curLoanpartIndex];
      }
    }
  }
}

const ComparisonFormData = new ComparisonFormStore();

export default ComparisonFormData;
